import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import SearchIcon from '../../assets/search_icon'
import styles from './searchBox.module.scss'
import classNames from 'classnames';
import type { JSX } from 'preact/jsx-runtime';
import type { IL10n } from '../../types/l10n';

const MAX_ARTICLE_NUMBER_LENGTH = 10;

const sanitizeSearchValue = (value: string) =>
  value.replace(/[^A-Za-z0-9äöüÄÖÜß]/g, '').slice(0, MAX_ARTICLE_NUMBER_LENGTH);

export function SearchBox({ l10n }: { l10n: IL10n }) {

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFocus = useCallback(() => {
    setFocus(true);
    searchBoxRef.current?.classList.add(styles.focus);
  }, []);
  const handleBlur = useCallback(() => {
    setFocus(false);
    searchBoxRef.current?.classList.remove(styles.focus);
  }, []);

  const clearMessage = useCallback(() => {
    setMessage("");
    setIsError(false);
  }, []);

  const handleChange = useCallback((event: JSX.TargetedEvent<HTMLInputElement, Event>) => {
    const cleaned = sanitizeSearchValue(event.currentTarget.value);
    setInput(cleaned);
    clearMessage();
  }, [clearMessage]);

  const handleInput = useCallback((event: JSX.TargetedEvent<HTMLInputElement, Event>) => {
    const raw = event.currentTarget.value;
    const cleaned = sanitizeSearchValue(raw);
    if (cleaned !== raw) {
      event.currentTarget.value = cleaned;
    }
    setInput(cleaned);
    clearMessage();
  }, [clearMessage]);

  const handleSubmit = async (event?: JSX.TargetedEvent<HTMLFormElement, Event>) => {
    event?.preventDefault();

    const salesArticleNo = input.trim().slice(0, MAX_ARTICLE_NUMBER_LENGTH);
    if (!salesArticleNo) {
      return;
    }

    clearMessage();
    setIsLoading(true);

    try {
      const response = await fetch(
        `api/datasheetretrieval/GetConformityFileData?salesArticleNo=${encodeURIComponent(salesArticleNo)}`
      );

      if (response.status === 404) {
        setIsError(true);
        setMessage(
          l10n.noResultFound
            ? l10n.noResultFound.replace('{0}', salesArticleNo)
            : `No conformity file found for article ${salesArticleNo}.`
        );
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data?.path) {
        setIsError(true);
        setMessage(
          l10n.noResultFound
            ? l10n.noResultFound.replace('{0}', salesArticleNo)
            : `No conformity file found for article ${salesArticleNo}.`
        );
        return;
      }

      if (data?.name) {
        setMessage(data.name);
        setIsError(false);
      }

      window.open(data.path, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to retrieve conformity file data', error);
      setIsError(true);
      setMessage(
        l10n.noResultFound
          ? l10n.noResultFound.replace('{0}', salesArticleNo)
          : `No conformity file found for article ${salesArticleNo}.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const activateFocusHelper = () => {
    handleFocus();
    inputRef.current?.focus();
  };

  function decodeHtmlEntities(htmlEntity: string): string {
    const decodedText = document.createElement("textarea");
    decodedText.innerHTML = htmlEntity;
    return decodedText.value;
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        handleBlur();
      } else {
        handleFocus();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleBlur, handleFocus]);
  return (
    <div class={styles.searchSection}>
      {message && isError && (
        <div class={styles.errorMessage}>
          <div dangerouslySetInnerHTML={{ __html: message }} />
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div class={classNames(styles.searchboxContainer, { [styles.focus]: focus })}
          ref={searchBoxRef}>
          <div class={styles.searchbox} focusable onClick={activateFocusHelper}>
            <button type="submit" class={styles.searchButton}>
              <SearchIcon />
            </button>
            <span class={styles.transitionWrapper}>
              <span
                class={styles.inputSizer}
              >
                <input
                  ref={inputRef}
                  value={input}
                  onInput={handleInput}
                  onChange={handleChange}
                  placeholder={focus ? "" : decodeHtmlEntities(l10n.searchPlaceholder)}
                  maxLength={MAX_ARTICLE_NUMBER_LENGTH}
                  class={styles.searchInput}
                  type="text"
                  name="query"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  aria-label={l10n.searchLabel}
                  aria-invalid={isError}
                />
              </span>
            </span>
          </div>
        </div >
      </form>
    </div>
  );
}